import { MONSTERS } from "./data.js";
import { NPC_LIST } from "./npcs.js";
/** ครึ่งความกว้างของแผนที่เมื่อโซนไม่กำหนด `half` */
export const DEFAULT_ZONE_HALF = 46;
/** ครึ่งความกว้างของทางเดิน (ทางกว้าง ~10 หน่วย — แคบพอให้รู้สึกเป็นทาง กว้างพอให้สู้กันได้) */
export const ROUTE_HALF = 5;
/** ระยะเผื่อรอบลานมอน */
const SPAWN_PAD = 4;
/** ลานโล่งรอบจุดเกิดและประตู */
const NODE_CLEAR = 9;
export function zoneHalf(zone) {
    return zone?.half ?? DEFAULT_ZONE_HALF;
}
function rng(seedStr) {
    let s = 1;
    for (const ch of seedStr)
        s = ((s * 31 + ch.charCodeAt(0)) % 2147483646) + 1;
    return () => (s = (s * 16807) % 2147483647) / 2147483647;
}
/** เส้นคดเคี้ยวจาก a ถึง b (ปลายทั้งสองตรงจุดพอดี) */
function winding(ax, az, bx, bz, rnd, lim) {
    const dx = bx - ax, dz = bz - az;
    const len = Math.hypot(dx, dz) || 1;
    const nx = -dz / len, nz = dx / len;
    const amp = Math.min(22, 4 + len * (0.18 + rnd() * 0.12));
    const ph1 = rnd() * Math.PI * 2, ph2 = rnd() * Math.PI * 2;
    const turns = 1 + rnd() * 1.5;
    const N = Math.max(6, Math.round(len / 5));
    const out = [];
    let px = ax, pz = az;
    for (let k = 1; k <= N; k++) {
        const t = k / N;
        const taper = Math.sin(Math.PI * t);
        const off = taper * amp * (Math.sin(t * Math.PI * 2 * turns + ph1) * 0.75 + Math.sin(t * Math.PI * 5 + ph2) * 0.25);
        let x = ax + dx * t + nx * off;
        let z = az + dz * t + nz * off;
        if (k === N) {
            x = bx;
            z = bz;
        }
        x = Math.max(-lim, Math.min(lim, x));
        z = Math.max(-lim, Math.min(lim, z));
        out.push([px, pz, x, z]);
        px = x;
        pz = z;
    }
    return out;
}
const routeCache = new Map();
/**
 * เครือข่ายทาง: ต้นไม้แผ่กิ่ง (Prim) เชื่อมจุดเกิด ลานมอนทุกลาน และประตูทุกบาน
 * แต่ละกิ่งเป็นทางคดเคี้ยว · เมืองไม่มี (เดินได้ทั่ว)
 */
export function routeNetwork(zone) {
    if (!zone || zone.town)
        return [];
    const hit = routeCache.get(zone.id);
    if (hit)
        return hit;
    const rnd = rng(zone.id);
    const lim = zoneHalf(zone) - 6;
    // NPC ในโซนนี้เป็นจุดที่ต้องเดินถึงด้วย · บอสเป็น "ปลายทาง" เท่านั้น
    // (ทางไม่ผ่านลานบอสไปที่อื่น — ผู้ใช้แจ้งว่าทางผ่านบอสแล้วเดินยาก)
    const nodes = [
        { x: zone.spawnPoint.x, z: zone.spawnPoint.z, leaf: false },
        ...zone.spawns.map((s) => ({ x: s.x, z: s.z, leaf: !!MONSTERS[s.monster]?.boss })),
        ...(zone.portals ?? []).map((p) => ({ x: p.x, z: p.z, leaf: false })),
        ...NPC_LIST.filter((n) => n.zone === zone.id).map((n) => ({ x: n.x, z: n.z, leaf: false })),
    ];
    const inTree = [0];
    const left = new Set(nodes.map((_, i) => i).slice(1));
    const segs = [];
    while (left.size) {
        let best = { a: 0, b: 0, d: Infinity };
        // บอสต่อเข้าเครือข่ายทีหลังสุดเสมอ (ไม่งั้นโหนดอื่นอาจต่อผ่านบอส)
        const pending = [...left].some((i) => !nodes[i].leaf);
        for (const a of inTree)
            for (const b of left) {
                if (nodes[a].leaf || (pending && nodes[b].leaf))
                    continue;
                const d = Math.hypot(nodes[a].x - nodes[b].x, nodes[a].z - nodes[b].z);
                if (d < best.d)
                    best = { a, b, d };
            }
        const A = nodes[best.a], B = nodes[best.b];
        segs.push(...winding(A.x, A.z, B.x, B.z, rnd, lim));
        inTree.push(best.b);
        left.delete(best.b);
    }
    routeCache.set(zone.id, segs);
    return segs;
}
export function distToSeg(x, z, [ax, az, bx, bz]) {
    const dx = bx - ax, dz = bz - az;
    const l2 = dx * dx + dz * dz || 1;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / l2));
    return Math.hypot(x - (ax + dx * t), z - (az + dz * t));
}
export function distToRoute(x, z, segs) {
    let best = Infinity;
    for (const s of segs) {
        const d = distToSeg(x, z, s);
        if (d < best)
            best = d;
    }
    return best;
}
/**
 * ระยะจากจุดถึงขอบพื้นที่เดินได้ (ติดลบ = อยู่ข้างใน) ใช้ปลูกป่าตามขอบใน world.ts
 */
export function wallDistance(zone, x, z) {
    const half = zoneHalf(zone);
    const edge = Math.max(Math.abs(x), Math.abs(z)) - (half - 1);
    if (!zone || zone.town)
        return edge;
    let d = distToRoute(x, z, routeNetwork(zone)) - ROUTE_HALF;
    for (const s of zone.spawns)
        d = Math.min(d, Math.hypot(x - s.x, z - s.z) - (s.radius + SPAWN_PAD));
    d = Math.min(d, Math.hypot(x - zone.spawnPoint.x, z - zone.spawnPoint.z) - NODE_CLEAR);
    for (const p of zone.portals ?? [])
        d = Math.min(d, Math.hypot(x - p.x, z - p.z) - NODE_CLEAR);
    return Math.max(d, edge);
}
export function walkable(zone, x, z) {
    return wallDistance(zone, x, z) <= 0;
}
/**
 * เดินจาก (x,z) ไป (nx,nz) — ชนป่าแล้วไถลไปตามแกนที่ยังไปได้ (เดินเฉียงชิดขอบไม่ติดหนึบ)
 * ถ้าตอนนี้อยู่นอกพื้นที่อยู่แล้ว (เช่น โดนสกิลพุ่ง) เดินได้เฉพาะทิศที่กลับเข้าหาทาง จะได้ไม่ติดค้าง
 */
export function slideMove(zone, x, z, nx, nz) {
    if (!walkable(zone, x, z)) {
        // อยู่นอกพื้นที่ (โดนพุ่ง/ข้อมูลเก่า) เดินได้เฉพาะทางที่พาเข้าใกล้ทาง ห้ามใช้ช่องนี้ท่องป่า
        return wallDistance(zone, nx, nz) <= wallDistance(zone, x, z) + 0.01 ? { x: nx, z: nz } : { x, z };
    }
    if (walkable(zone, nx, nz))
        return { x: nx, z: nz };
    if (walkable(zone, nx, z))
        return { x: nx, z };
    if (walkable(zone, x, nz))
        return { x, z: nz };
    return { x, z };
}
/* ---------- หาทางเดินตามทาง (คลิกไกล ๆ แล้วเดินอ้อมป่าเอง) ---------- */
/** ขนาดช่องตารางหาทาง (หน่วยโลก) */
const NAV_STEP = 2;
const navCache = new Map();
function navGrid(zone) {
    const hit = navCache.get(zone.id);
    if (hit)
        return hit;
    const half = zoneHalf(zone);
    const n = Math.floor((half * 2) / NAV_STEP) + 1;
    const open = new Uint8Array(n * n);
    for (let i = 0; i < n; i++)
        for (let j = 0; j < n; j++) {
            // ช่องต้องห่างขอบป่าพอ ไม่งั้นสองช่องที่ติดกันอาจมีแนวป่าบาง ๆ คั่นตรงกลาง (เดินจริงแล้วติด)
            open[i * n + j] = wallDistance(zone, i * NAV_STEP - half, j * NAV_STEP - half) <= -1.2 ? 1 : 0;
        }
    const g = { n, half, open };
    navCache.set(zone.id, g);
    return g;
}
/** เดินเส้นตรงถึงกันได้โดยไม่เฉียดป่า (ตรวจทุก 0.4 หน่วย) */
export function clearLine(zone, ax, az, bx, bz) {
    const d = Math.hypot(bx - ax, bz - az);
    const k = Math.max(1, Math.ceil(d / 0.4));
    // เว้นระยะจากขอบป่าเล็กน้อย เส้นที่เฉียดขอบทำให้เดินจริงทีละก้าวแล้วติดมุม
    // ใช้ตารางระยะที่คิดไว้แล้ว (wallDistanceFast) — เดิมคิด wallDistance เต็มทุกจุด findPath ครั้งละ ~16ms
    // ซึ่งหนักเกินเมื่อมอนบน server ไล่ตามทาง (audit รอบ 18) · ค่าคลาดได้ไม่เกินครึ่งช่อง เผื่อไว้ในขอบ -0.6 แล้ว
    for (let i = 1; i < k; i++) {
        if (wallDistanceFast(zone, ax + ((bx - ax) * i) / k, az + ((bz - az) * i) / k) > -0.6)
            return false;
    }
    return walkable(zone, bx, bz);
}
/** ความละเอียดตารางระยะถึงป่า (หน่วยโลก) */
const WD_STEP = 1;
const wdCache = new Map();
/** wallDistance แบบอ่านจากตาราง (bilinear) — ใช้หาทาง ไม่ใช้ตัดสินการเดิน (server ใช้ wallDistance ตัวจริงเสมอ) */
export function wallDistanceFast(zone, x, z) {
    if (!zone || zone.town)
        return wallDistance(zone, x, z);
    let c = wdCache.get(zone.id);
    if (!c) {
        const half = zoneHalf(zone);
        const n = Math.floor((half * 2) / WD_STEP) + 1;
        const d = new Float32Array(n * n);
        for (let i = 0; i < n; i++)
            for (let j = 0; j < n; j++)
                d[i * n + j] = wallDistance(zone, i * WD_STEP - half, j * WD_STEP - half);
        c = { n, half, d };
        wdCache.set(zone.id, c);
    }
    const fx = (x + c.half) / WD_STEP, fz = (z + c.half) / WD_STEP;
    if (fx < 0 || fz < 0 || fx >= c.n - 1 || fz >= c.n - 1)
        return wallDistance(zone, x, z);
    const i = Math.floor(fx), j = Math.floor(fz), tx = fx - i, tz = fz - j, n = c.n, d = c.d;
    const a00 = d[i * n + j], a01 = d[i * n + j + 1], a10 = d[(i + 1) * n + j], a11 = d[(i + 1) * n + j + 1];
    return (a00 * (1 - tz) + a01 * tz) * (1 - tx) + (a10 * (1 - tz) + a11 * tz) * tx;
}
/**
 * หาทางจาก a ไป b ตามพื้นที่เดินได้ (A* บนตาราง 8 ทิศ แล้วตัดจุดที่มองตรงถึงกันได้ทิ้ง)
 * คืนรายการจุดแวะ (ไม่รวมจุดเริ่ม) · ปลายทางอยู่ในป่า = ไปจุดเดินได้ที่ใกล้ที่สุด · หาไม่เจอ = []
 * **เป็นแค่ความตั้งใจฝั่ง client** server ยังตัดสินทุกก้าวด้วย slideMove เหมือนเดิม
 */
export function findPath(zone, ax, az, bx, bz) {
    if (!zone || zone.town || clearLine(zone, ax, az, bx, bz))
        return [{ x: bx, z: bz }];
    const { n, half, open } = navGrid(zone);
    const toI = (v) => Math.max(0, Math.min(n - 1, Math.round((v + half) / NAV_STEP)));
    const nearestOpen = (i0, j0) => {
        if (open[i0 * n + j0])
            return i0 * n + j0;
        for (let r = 1; r < 12; r++) {
            for (let di = -r; di <= r; di++)
                for (let dj = -r; dj <= r; dj++) {
                    const i = i0 + di, j = j0 + dj;
                    if (i >= 0 && j >= 0 && i < n && j < n && open[i * n + j])
                        return i * n + j;
                }
        }
        return -1;
    };
    const start = nearestOpen(toI(ax), toI(az));
    const goal = nearestOpen(toI(bx), toI(bz));
    if (start < 0 || goal < 0)
        return [];
    const gi = Math.floor(goal / n), gj = goal % n;
    const g = new Float32Array(n * n).fill(Infinity);
    const from = new Int32Array(n * n).fill(-1);
    const closed = new Uint8Array(n * n);
    // คิวลำดับความสำคัญแบบ binary heap
    const heap = [];
    const push = (f, c) => {
        heap.push([f, c]);
        let i = heap.length - 1;
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (heap[p][0] <= heap[i][0])
                break;
            [heap[p], heap[i]] = [heap[i], heap[p]];
            i = p;
        }
    };
    const pop = () => {
        const top = heap[0];
        const last = heap.pop();
        if (heap.length) {
            heap[0] = last;
            let i = 0;
            for (;;) {
                const l = i * 2 + 1, r = l + 1;
                let m = i;
                if (l < heap.length && heap[l][0] < heap[m][0])
                    m = l;
                if (r < heap.length && heap[r][0] < heap[m][0])
                    m = r;
                if (m === i)
                    break;
                [heap[m], heap[i]] = [heap[i], heap[m]];
                i = m;
            }
        }
        return top;
    };
    const h = (c) => Math.hypot(Math.floor(c / n) - gi, (c % n) - gj);
    g[start] = 0;
    push(h(start), start);
    while (heap.length) {
        const [, c] = pop();
        if (closed[c])
            continue;
        closed[c] = 1;
        if (c === goal)
            break;
        const ci = Math.floor(c / n), cj = c % n;
        for (let di = -1; di <= 1; di++)
            for (let dj = -1; dj <= 1; dj++) {
                if (!di && !dj)
                    continue;
                const i = ci + di, j = cj + dj;
                if (i < 0 || j < 0 || i >= n || j >= n)
                    continue;
                const nc = i * n + j;
                if (!open[nc] || closed[nc])
                    continue;
                // ห้ามตัดมุมป่า
                if (di && dj && (!open[ci * n + j] || !open[i * n + cj]))
                    continue;
                const ng = g[c] + (di && dj ? Math.SQRT2 : 1);
                if (ng < g[nc]) {
                    g[nc] = ng;
                    from[nc] = c;
                    push(ng + h(nc), nc);
                }
            }
    }
    if (!closed[goal])
        return [];
    const cells = [];
    for (let c = goal; c !== -1; c = from[c])
        cells.push(c);
    cells.reverse();
    const pts = cells.map((c) => ({ x: Math.floor(c / n) * NAV_STEP - half, z: (c % n) * NAV_STEP - half }));
    if (walkable(zone, bx, bz))
        pts[pts.length - 1] = { x: bx, z: bz };
    // ตัดจุดที่มองตรงถึงกันได้ ให้เดินเป็นเส้นเรียบ ไม่ซิกแซกตามตาราง
    const out = [];
    let cx = ax, cz = az, k = 0;
    while (k < pts.length) {
        let far = k;
        for (let t = pts.length - 1; t > k; t--) {
            if (clearLine(zone, cx, cz, pts[t].x, pts[t].z)) {
                far = t;
                break;
            }
        }
        out.push(pts[far]);
        cx = pts[far].x;
        cz = pts[far].z;
        k = far + 1;
    }
    return out;
}
//# sourceMappingURL=terrain.js.map